import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GuestForm from './guestForm';
import AuthForm from './authform';
import supabase from '../../supabase';
import FullPageLoader from '../FullPageLoader';

export default function ReviewPage({ isGuest }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    async function verifyInvite() {
      setLoading(true);
      setError(null);

      try {
        if (!token) throw new Error("Access Denied: Invitation token required.");

        // 1. IDENTITY & REDIRECT CHECK
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;

        // CASE A: Logged in but on Guest URL -> Redirect the browser
        if (user && isGuest) {
          navigate(`/verify-feedback/authorized/${token}`, { replace: true });
          return; // Exit early; the URL change will re-trigger this useEffect
        }

        // CASE B: Logged out but on Authorized URL -> Set the bait and Sign In
        if (!user && !isGuest) {
          sessionStorage.setItem('pending_review_token', token);
          sessionStorage.setItem('review_type', 'authorized');
          navigate('/signin', { replace: true });
          return;
        }

        // 2. FETCH INVITE DATA
        const { data: invite, error: dbError } = await supabase
          .from('review_invites')
          .select('*, businesses(*)')
          .eq('token', token)
          .single();

        if (dbError || !invite) {
          console.error("DB Error:", dbError);
          throw new Error("Invalid or expired invitation.");
        }
        
        if (invite.status === 'completed') {
          throw new Error("This review link has already been used.");
        }

        // 3. CLEANUP BAIT (If they successfully reached Authorized flow)
        if (user && !isGuest) {
          sessionStorage.removeItem('pending_review_token');
          sessionStorage.removeItem('review_type');
        }

        setData(invite);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    verifyInvite();
  }, [token, isGuest, navigate]);

  if (loading) return <FullPageLoader />;
  
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f3f3f3]">
      <div className="p-10 text-center text-red-600 font-bold bg-white rounded-xl shadow-lg max-w-md mx-4">
        {error}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-[#f3f3f3] flex items-center justify-center py-10 px-4">
      {isGuest ? (
        <GuestForm token={token} invite={data} />
      ) : (
        <AuthForm token={token} invite={data} />
      )}
    </div>
  );
}